import { useState } from "react";

import { submitAiFeedback } from "./aiApi";
import { isDemoMockAnalysis } from "./demoMockAnalysis";
import { physicalFinancialLabel } from "../financialProjection/cashFlowPlanning";
import { normalizeText } from "../disbursement/engine";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const percentageFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "America/Sao_Paulo",
});

const currency = (cents) =>
  Number.isSafeInteger(cents)
    ? currencyFormatter.format(cents / 100).replace(/\u00a0/g, " ")
    : "Sem dado";

const signedCurrency = (cents) => {
  if (!Number.isSafeInteger(cents)) return "Sem comparação";
  const prefix = cents > 0 ? "+" : "";
  return `${prefix}${currency(cents)}`;
};

const signedPercentage = (basisPoints) => {
  if (!Number.isSafeInteger(basisPoints)) return null;
  const prefix = basisPoints > 0 ? "+" : "";
  return `${prefix}${percentageFormatter.format(basisPoints / 100)}%`;
};

const percentage = (basisPoints) =>
  Number.isSafeInteger(basisPoints)
    ? `${percentageFormatter.format(basisPoints / 100)}%`
    : null;

const competenceLabel = (competence) => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(String(competence ?? ""));
  return match
    ? monthFormatter.format(
        new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)),
      )
    : "competência anterior";
};

const analysisDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : dateTimeFormatter.format(date);
};

const evidenceLabel = (evidence) => {
  if (evidence?.kind === "financial_eap_driver") {
    return `${evidence.name} · diferença ${signedCurrency(
      evidence.varianceCents,
    )}`;
  }
  if (evidence?.kind === "financial_creditor_driver") {
    return `${evidence.name} · ${evidence.document} · pago ${currency(
      evidence.paidCents,
    )}`;
  }
  if (evidence?.kind === "financial_neighbor_month") {
    return `Comparação com ${competenceLabel(evidence.competence)}`;
  }
  if (evidence?.kind === "financial_quality")
    return "Cobertura e consistência do detalhamento";
  if (evidence?.kind === "financial_month_summary")
    return "Resumo financeiro da competência";
  return "Evidência financeira do Ampli";
};

const Metric = ({ label, value, detail }) => (
  <article
    className="financial-month-ai-metric"
    data-testid="financial-month-metric"
  >
    <h4>{label}</h4>
    <strong>{currency(value)}</strong>
    {detail ? <small>{detail}</small> : null}
  </article>
);

const Comparison = ({
  title,
  output,
  difference,
  percentage: percentageBps,
  basis,
}) => (
  <article className="financial-month-ai-comparison">
    <h4>{title}</h4>
    <div className="financial-month-ai-comparison__value">
      <strong>{signedCurrency(difference)}</strong>
      {signedPercentage(percentageBps) ? (
        <span>{signedPercentage(percentageBps)}</span>
      ) : null}
    </div>
    {Number.isSafeInteger(basis) ? (
      <small>Base comparada: {currency(basis)}</small>
    ) : null}
    <p>{output?.explanation}</p>
  </article>
);

const AMPLI_KINDS = {
  summary: "ampli_disbursement_month_summary",
  root: "ampli_disbursement_root",
  rule: "ampli_disbursement_rule",
  adjustment: "ampli_disbursement_adjustment",
};
const validAmpliEntry = (entry) => {
  const parts = String(entry?.ref ?? "").split("|");
  return (
    parts.length === 4 &&
    parts[0] === "ampli" &&
    /^\d{4}-(0[1-9]|1[0-2])$/.test(parts[1]) &&
    entry.competence === parts[1] &&
    AMPLI_KINDS[parts[2]] === entry.kind &&
    /^[A-Za-z0-9_-]{43}$/.test(parts[3])
  );
};
const editableRuleKey = (key) => {
  if (
    typeof key !== "string" ||
    key.length > 300 ||
    /[\p{Cc}\u2028\u2029]/u.test(key)
  )
    return false;
  if (key.startsWith("node:")) return /^node:[A-Za-z0-9]+$/.test(key);
  if (key.startsWith("resource:")) return /^resource:[^:\s]+$/.test(key);
  const group = /^resource_group:(erp|ampli):(.+)$/.exec(key);
  return Boolean(
    group &&
      (group[1] === "erp"
        ? normalizeText(group[2]).length > 0
        : group[2].trim().length > 0),
  );
};

const AmpliEvidenceEntry = ({ entry, onAdjustRule }) => {
  if (entry.kind === AMPLI_KINDS.summary) {
    return (
      <>
        <strong>
          {entry.quality === "complete"
            ? `Previsão de desembolso: ${currency(entry.totalCents)}`
            : "Total da obra indisponível"}
        </strong>
        {entry.quality !== "complete" &&
        Number.isSafeInteger(entry.availableTotals?.totalCents) ? (
          <p>{`Subtotal disponível: ${currency(
            entry.availableTotals.totalCents,
          )}`}</p>
        ) : null}
        <p>{`Raízes com dados: ${entry.explainedRootCount ?? "—"} de ${
          entry.rootCount ?? "—"
        }.`}</p>
        {entry.truncated ? (
          <p>
            A análise atingiu o limite de raízes; parte da obra não foi
            incluída.
          </p>
        ) : null}
        {entry.quality !== "complete" ? (
          <p>
            Há fontes ausentes ou degradadas; os valores disponíveis não
            representam o total completo da obra.
          </p>
        ) : null}
      </>
    );
  }
  if (entry.kind === AMPLI_KINDS.root) {
    return (
      <>
        <strong>{`Raiz ${entry.wbsCodeId}`}</strong>
        <p>
          {entry.available && !entry.reason
            ? currency(entry.totalCents)
            : "Proveniência desta raiz indisponível ou parcial."}
        </p>
        {entry.reason &&
        Number.isSafeInteger(entry.availableTotals?.totalCents) ? (
          <p>{`Subtotal da raiz disponível: ${currency(
            entry.availableTotals.totalCents,
          )}`}</p>
        ) : null}
      </>
    );
  }
  if (entry.kind === AMPLI_KINDS.rule) {
    const missingLabel =
      entry.ruleKey === null ? "Pagamento à vista" : "Regra sem descrição";
    return (
      <>
        <strong>{entry.ruleLabel ?? missingLabel}</strong>
        <p>{`${currency(entry.cents)} · origem em ${competenceLabel(
          entry.sourceCompetence,
        )} · raiz ${entry.wbsCodeId}`}</p>
        {onAdjustRule && editableRuleKey(entry.ruleKey) ? (
          <button
            type="button"
            aria-label={`Ajustar regra: ${entry.ruleLabel ?? entry.ruleKey}`}
            onClick={() => onAdjustRule(entry.ruleKey)}
          >
            Ajustar regra
          </button>
        ) : null}
      </>
    );
  }
  return (
    <>
      <strong>{`Ajuste de ${competenceLabel(
        entry.fromCompetence,
      )} para ${competenceLabel(entry.toCompetence)}`}</strong>
      <p>{`Aplicado: ${currency(entry.appliedCents)} · excedente: ${currency(
        entry.exceedsCents,
      )} · raiz ${entry.wbsCodeId}`}</p>
    </>
  );
};

const AmpliEvidence = ({ entries, onAdjustRule }) => (
  <section
    aria-label="Proveniência do desembolso Ampli"
    className="financial-month-ai-output-drivers"
  >
    <h4>De onde veio o desembolso Ampli</h4>
    {entries.map((entry) => (
      <article key={entry.ref}>
        <AmpliEvidenceEntry entry={entry} onAdjustRule={onAdjustRule} />
      </article>
    ))}
  </section>
);

const FinancialMonthAiResult = ({
  analysis,
  readOnly = false,
  workId,
  onAdjustRule,
}) => {
  const [feedback, setFeedback] = useState(null);
  if (!analysis?.output) return null;

  const demoMock = isDemoMockAnalysis(analysis);
  const { output } = analysis;
  const evidence = Array.isArray(analysis.evidence) ? analysis.evidence : [];
  const summary = evidence.find(
    (item) => item.kind === "financial_month_summary",
  );
  const usesPhysicalFinancial =
    summary?.cashFlowPlanningSource === "physical_financial";
  const usesCombined = summary?.cashFlowPlanningSource === "combined";
  const canonicalPhysicalLabel = physicalFinancialLabel({
    physicalFinancial: summary?.physicalFinancial,
  });
  const planningComparisonLabel = usesPhysicalFinancial
    ? "Previsto"
    : "desembolso previsto";
  const planningMetricLabel = usesPhysicalFinancial
    ? "Previsto"
    : "Desembolso planejado";
  const eapDrivers = evidence.filter(
    (item) => item.kind === "financial_eap_driver",
  );
  const creditors = evidence.filter(
    (item) => item.kind === "financial_creditor_driver",
  );
  const quality = evidence.find((item) => item.kind === "financial_quality");
  const previous = evidence.find(
    (item) => item.kind === "financial_neighbor_month",
  );
  const values = summary?.values ?? {};
  const comparisons = summary?.comparisons ?? {};
  const evidenceByRef = new Map(evidence.map((item) => [item.ref, item]));
  const citedRefs = new Set([
    ...(output.drivers ?? []).flatMap((item) => item.evidenceRefs ?? []),
    ...[
      output.paidVsDisbursement,
      output.paidVsMeasured,
      output.paidVsPhysicalFinancial,
      output.measuredVsScheduled,
    ].flatMap((item) => item?.evidenceRefs ?? []),
  ]);
  const ampliEntries = evidence.filter(
    (entry) =>
      validAmpliEntry(entry) &&
      evidence.filter((other) => other.ref === entry.ref).length === 1 &&
      ([AMPLI_KINDS.summary, AMPLI_KINDS.root].includes(entry.kind) ||
        citedRefs.has(entry.ref)),
  );
  const referencedPrevious = output.drivers?.some((driver) =>
    driver.evidenceRefs?.includes(previous?.ref),
  );

  const saveFeedback = async (helpful) => {
    setFeedback("saving");
    try {
      await submitAiFeedback(workId, analysis.analysisId, {
        helpful,
        comment: "",
      });
      setFeedback("saved");
    } catch {
      setFeedback("error");
    }
  };

  return (
    <section
      className="item-ai-result financial-month-ai-result"
      aria-label="Resultado da análise financeira com IA"
    >
      {demoMock ? (
        <p
          aria-label="Dados demonstrativos simulados"
          className="item-ai-provider-warning item-ai-provider-warning--demo"
          role="status"
        >
          <strong>
            Dados demonstrativos simulados — não representam operação financeira
            real.
          </strong>
          <span>Modelo: mock-demo</span>
        </p>
      ) : null}
      {analysis.status === "provider_unavailable" ? (
        <p className="item-ai-provider-warning" role="status">
          A IA não respondeu, mas os cálculos mensais do Ampli continuam
          disponíveis. Esta tentativa não consumiu sua cota.
        </p>
      ) : null}
      {analysis.status === "insufficient_data" ? (
        <p className="item-ai-provider-warning" role="status">
          Ainda não há séries mensais comparáveis suficientes. Esta tentativa
          não consumiu sua cota.
        </p>
      ) : null}

      <div className="item-ai-conclusion">
        <span>Leitura do mês</span>
        <p>{output.conclusion}</p>
      </div>
      {ampliEntries.length > 0 ? (
        <AmpliEvidence
          entries={ampliEntries}
          onAdjustRule={
            !readOnly &&
            !demoMock &&
            (!analysis.workId || analysis.workId === workId)
              ? onAdjustRule
              : undefined
          }
        />
      ) : null}

      {analysis.question ||
      analysis.createdAt ||
      analysis.calculationRevision ? (
        <div className="financial-month-ai-metadata">
          <strong>Contexto da análise</strong>
          {analysis.question ? <span>{analysis.question}</span> : null}
          <small>
            {[
              analysisDate(analysis.createdAt),
              analysis.calculationRevision
                ? `Revisão ${analysis.calculationRevision}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </small>
        </div>
      ) : null}

      <div
        className="financial-month-ai-metrics"
        aria-label="Valores da competência"
      >
        <Metric
          label="Previsto"
          value={values.scheduledCents}
          detail="Previsão físico-financeira"
        />
        <Metric
          label="Medido"
          value={values.measuredCents}
          detail="Execução reconhecida"
        />
        <Metric
          label="Pago"
          value={values.paidCents}
          detail="Saída realizada"
        />
        <Metric
          label="A pagar"
          value={values.toPayCents}
          detail="Compromissos pendentes"
        />
      </div>

      <div
        className="financial-month-ai-comparisons"
        aria-label="Comparações da competência"
      >
        <Comparison
          title={`Pago × ${planningComparisonLabel}`}
          output={output.paidVsDisbursement}
          difference={comparisons.paidVsDisbursementCents}
          percentage={comparisons.paidVsDisbursementBps}
          basis={values.plannedDisbursementCents}
        />
        {usesCombined ? (
          <Comparison
            title={`Pago × ${canonicalPhysicalLabel}`}
            output={output.paidVsPhysicalFinancial}
            difference={comparisons.paidVsScheduledCents}
            percentage={comparisons.paidVsScheduledBps}
            basis={values.scheduledCents}
          />
        ) : null}
        <Comparison
          title="Pago × medido"
          output={output.paidVsMeasured}
          difference={comparisons.paidVsMeasuredCents}
          percentage={comparisons.paidVsMeasuredBps}
          basis={values.measuredCents}
        />
        <Comparison
          title="Medido × previsto"
          output={output.measuredVsScheduled}
          difference={comparisons.measuredVsScheduledCents}
          percentage={comparisons.measuredVsScheduledBps}
          basis={values.scheduledCents}
        />
      </div>

      {output.drivers?.length > 0 ? (
        <div className="financial-month-ai-output-drivers">
          <h4>Direcionadores da análise</h4>
          <div>
            {output.drivers.map((driver, index) => (
              <article key={`${driver.kind}-${index}`}>
                <p>{driver.explanation}</p>
                <ul>
                  {[...new Set(driver.evidenceRefs)]
                    .filter((ref) => !validAmpliEntry(evidenceByRef.get(ref)))
                    .map((ref) => (
                      <li key={ref}>{evidenceLabel(evidenceByRef.get(ref))}</li>
                    ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {referencedPrevious ? (
        <div className="financial-month-ai-previous">
          <h4>Mês anterior: {competenceLabel(previous.competence)}</h4>
          <div>
            <Metric label="Medido" value={previous.measuredCents} />
            <Metric label="Pago" value={previous.paidCents} />
            <Metric
              label={planningMetricLabel}
              value={previous.plannedDisbursementCents}
            />
          </div>
        </div>
      ) : null}

      {eapDrivers.length > 0 ? (
        <div className="financial-month-ai-drivers">
          <h4>EAPs que mais explicam o mês</h4>
          <div className="financial-month-ai-table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">EAP</th>
                  <th scope="col">Pago</th>
                  <th scope="col">{planningMetricLabel}</th>
                  <th scope="col">Medido</th>
                  <th scope="col">Diferença</th>
                </tr>
              </thead>
              <tbody>
                {eapDrivers.map((driver) => (
                  <tr key={driver.ref}>
                    <th scope="row">
                      <span>{driver.name}</span>
                      <small>{driver.code}</small>
                    </th>
                    <td>{currency(driver.paidCents)}</td>
                    <td>{currency(driver.plannedDisbursementCents)}</td>
                    <td>{currency(driver.measuredCents)}</td>
                    <td>{signedCurrency(driver.varianceCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {creditors.length > 0 ? (
        <div className="financial-month-ai-creditors">
          <h4>Pagamentos concentrados por fornecedor</h4>
          <div className="financial-month-ai-creditor-grid">
            {creditors.map((creditor) => (
              <article key={creditor.ref}>
                <span>{creditor.name}</span>
                <strong>{currency(creditor.paidCents)}</strong>
                <small>
                  {creditor.document} · {creditor.entries} lançamento
                  {creditor.entries === 1 ? "" : "s"}
                </small>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {quality ? (
        <p className="financial-month-ai-coverage">
          Cobertura do detalhamento por EAP: pago{" "}
          {percentage(quality.eapPaidCoverageBps) || "sem dado"},{" "}
          {usesPhysicalFinancial ? "previsto" : "desembolso"}{" "}
          {percentage(quality.eapDisbursementCoverageBps) || "sem dado"} e{" "}
          medido {percentage(quality.eapMeasuredCoverageBps) || "sem dado"}.
        </p>
      ) : null}

      {output.limitations?.length > 0 ? (
        <div className="item-ai-list">
          <h4>Limitações</h4>
          <ul>
            {output.limitations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="item-ai-list">
        <h4>O que conferir agora</h4>
        <ol>
          {output.nextSteps?.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      </div>

      {analysis.state === "completed" && !demoMock && !readOnly ? (
        <div className="item-ai-feedback">
          <span>Esta análise ajudou?</span>
          <button
            type="button"
            onClick={() => saveFeedback(true)}
            disabled={feedback === "saving"}
          >
            Sim
          </button>
          <button
            type="button"
            onClick={() => saveFeedback(false)}
            disabled={feedback === "saving"}
          >
            Não
          </button>
          {feedback === "saved" ? <small>Obrigado pelo retorno.</small> : null}
          {feedback === "error" ? (
            <small>Não foi possível registrar agora.</small>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default FinancialMonthAiResult;
